import type { Camera } from 'three';
import { Vector3 } from 'three';
import type { Graph3DVector3 } from '../core/graph.types';

/**
 * 坐标转换工具。
 *
 * 渲染期存在三套坐标：
 * 1. DOM 像素坐标：普通 HTML 元素的 CSS px（如 canvas.measureText 的结果、元素的 width/height style）。
 * 2. 屏幕/NDC 坐标：three.js 相机投影后的归一化坐标，范围 [-1, 1]，用于非 transform 模式下 Html 的定位计算。
 * 3. 世界坐标：three.js 场景中的世界单位（如 node.resolvedPosition、node.resolvedSize）。
 *
 * 这里重点覆盖 drei `<Html transform>` 用到的「DOM 像素 ↔ 世界单位」换算：
 * drei 在 transform 模式下会把 Html 内容按 `distanceFactor / 400` 缩放到世界单位
 * （见 @react-three/drei 的 web/Html.js 源码），也就是说只有当 distanceFactor = 400 时，
 * 1 CSS px 才等于 1 个世界单位。textMetrics.ts / autoSizeNodes.ts 里基于
 * canvas.measureText 计算出的节点宽高，正是按「1 CSS px = 1 世界单位」假设来预留空间的，
 * 因此配套的 `<Html transform>` 必须使用 HTML_TRANSFORM_BASE_DISTANCE_FACTOR，
 * 否则文字实际渲染尺寸与预留的节点框尺寸会不一致（要么溢出，要么留白过多）。
 *
 * 这些函数只服务渲染期的坐标换算，不参与 layout/ 下的节点位置与尺寸计算，
 * 因此放在 utils/ 而不是 layout/。
 */
export const HTML_TRANSFORM_BASE_DISTANCE_FACTOR = 400;

/**
 * 计算 drei `<Html transform>` 在给定 distanceFactor 下的缩放比例（世界单位 / CSS px）。
 * 当 distanceFactor 等于 HTML_TRANSFORM_BASE_DISTANCE_FACTOR 时，缩放比例为 1，
 * 即 1 CSS px = 1 世界单位。
 */
export function getHtmlTransformScale(distanceFactor: number): number {
  return distanceFactor / HTML_TRANSFORM_BASE_DISTANCE_FACTOR;
}

/**
 * 将 CSS 像素值换算为给定 distanceFactor 下 `<Html transform>` 渲染出的世界单位。
 * 例如：节点框按世界单位定义了宽度，若 Html 内容使用非 400 的 distanceFactor，
 * 可以用这个函数反推该内容在世界坐标里实际占用的尺寸。
 */
export function cssPixelToWorldUnit(px: number, distanceFactor: number): number {
  return px * getHtmlTransformScale(distanceFactor);
}

/**
 * 将世界单位换算为给定 distanceFactor 下 `<Html transform>` 应设置的 CSS 像素值，
 * 即 cssPixelToWorldUnit 的逆运算。
 */
export function worldUnitToCssPixel(units: number, distanceFactor: number): number {
  return units / getHtmlTransformScale(distanceFactor);
}

export interface NdcPoint {
  x: number;
  y: number;
  z: number;
}

/**
 * 将世界坐标投影为屏幕 NDC 坐标（[-1, 1] 范围），用于非 transform 模式下的
 * Html/Sprite 定位计算，或需要判断节点是否在视锥内的场景。
 */
export function projectWorldToNdc(position: Graph3DVector3, camera: Camera): NdcPoint {
  const vector = new Vector3(position.x, position.y, position.z);
  vector.project(camera);
  return { x: vector.x, y: vector.y, z: vector.z };
}

/**
 * 将屏幕 NDC 坐标换算为 DOM 像素坐标（以视口左上角为原点），
 * 与 projectWorldToNdc 搭配使用即可从世界坐标得到该点在页面上的像素位置。
 */
export function ndcToScreenPixel(
  ndc: { x: number; y: number },
  viewportWidth: number,
  viewportHeight: number
): { x: number; y: number } {
  return {
    x: ((ndc.x + 1) / 2) * viewportWidth,
    y: ((1 - ndc.y) / 2) * viewportHeight,
  };
}
